/**
 * Stripe Error Utilities
 *
 * Handles Stripe-specific error codes and provides user-friendly messages
 */

/**
 * Stripe error code to user-friendly message mapping
 */
export const STRIPE_ERROR_MESSAGES = {
  // Card errors
  card_declined: 'Your card was declined. Please try another payment method.',
  expired_card: 'Your card has expired. Please use a different card.',
  incorrect_cvc: 'The security code is incorrect. Please check and try again.',
  incorrect_number: 'The card number is incorrect. Please check and try again.',
  invalid_number: 'The card number is not valid. Please check and try again.',
  invalid_expiry_year: 'The expiration year is invalid.',
  invalid_expiry_month: 'The expiration month is invalid.',
  insufficient_funds: 'Insufficient funds. Please use a different payment method.',
  invalid_cvc: 'The security code is invalid.',
  invalid_expiry_year_past: 'The expiration year is in the past.',

  // Processing errors
  processing_error: 'An error occurred while processing your card. Please try again.',
  payment_intent_authentication_failure: 'Authentication failed. Please try again.',
  payment_intent_payment_attempt_failed: 'Payment attempt failed. Please try again.',

  // Network errors
  api_connection_error: 'Network error. Please check your connection and try again.',
  api_error: 'An error occurred with our payment provider. Please try again.',

  // Rate limiting
  rate_limit: 'Too many requests. Please wait a moment and try again.',

  // Generic
  generic_decline: 'Your payment was declined. Please try another payment method.',
  validation_error: 'Please check your payment details and try again.',
};

/**
 * Get user-friendly error message from Stripe error
 *
 * @param {Object|Error} error - Stripe error object
 * @returns {string} User-friendly error message
 */
export function getStripeErrorMessage(error) {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Stripe error with code
  if (error.code && STRIPE_ERROR_MESSAGES[error.code]) {
    return STRIPE_ERROR_MESSAGES[error.code];
  }

  // Stripe error with type
  if (error.type) {
    switch (error.type) {
      case 'validation_error':
        return error.message || STRIPE_ERROR_MESSAGES.validation_error;

      case 'card_error':
        return error.message || STRIPE_ERROR_MESSAGES.card_declined;

      case 'api_connection_error':
        return STRIPE_ERROR_MESSAGES.api_connection_error;

      case 'api_error':
        return STRIPE_ERROR_MESSAGES.api_error;

      case 'rate_limit_error':
        return STRIPE_ERROR_MESSAGES.rate_limit;

      default:
        break;
    }
  }

  // Use error message if available and safe
  if (error.message) {
    // Don't expose internal error details
    const sensitiveKeywords = [
      'secret',
      'key',
      'token',
      'internal',
      'server error',
      'undefined',
      'null'
    ];

    const isSensitive = sensitiveKeywords.some(keyword =>
      error.message.toLowerCase().includes(keyword)
    );

    if (!isSensitive) {
      return error.message;
    }
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Check if error is a network error
 *
 * @param {Object|Error} error - Error object
 * @returns {boolean} True if network error
 */
export function isNetworkError(error) {
  return (
    error?.type === 'api_connection_error' ||
    error?.code === 'api_connection_error' ||
    error?.code === 'network_error' ||
    !error?.type
  );
}

/**
 * Check if error is retryable
 *
 * Some errors are temporary and the user should be encouraged to retry
 *
 * @param {Object|Error} error - Error object
 * @returns {boolean} True if error is retryable
 */
export function isRetryableError(error) {
  const retryableCodes = [
    'processing_error',
    'api_connection_error',
    'api_error',
    'rate_limit',
  ];

  const retryableTypes = [
    'api_connection_error',
    'api_error',
    'rate_limit_error',
  ];

  return (
    retryableCodes.includes(error?.code) ||
    retryableTypes.includes(error?.type)
  );
}

/**
 * Check if error requires a different payment method
 *
 * @param {Object|Error} error - Error object
 * @returns {boolean} True if different payment method recommended
 */
export function requiresDifferentPaymentMethod(error) {
  const codes = [
    'card_declined',
    'expired_card',
    'insufficient_funds',
    'generic_decline',
  ];

  return codes.includes(error?.code);
}

/**
 * Log Stripe error for debugging
 *
 * @param {string} context - Context where error occurred
 * @param {Object|Error} error - Error object
 */
export function logStripeError(context, error) {
  if (process.env.NODE_ENV === 'development') {
    console.group(`[Stripe Error] ${context}`);
    console.error('Error object:', error);
    console.log('Error code:', error?.code);
    console.log('Error type:', error?.type);
    console.log('Error message:', error?.message);
    console.log('User-friendly message:', getStripeErrorMessage(error));
    console.groupEnd();
  }
}

const stripeErrorUtils = {
  STRIPE_ERROR_MESSAGES,
  getStripeErrorMessage,
  isNetworkError,
  isRetryableError,
  requiresDifferentPaymentMethod,
  logStripeError,
};

export default stripeErrorUtils;
